import { BASE_URL, getAuthHeaders, readApiError } from './auth';

export type StandaloneGameType = 'flipCardGame' | 'quizMarathon' | 'doubleDown';
export type GameStatus = 'active' | 'archived';

export interface GameCategoryOption {
    id: number;
    name: string;
    description?: string | null;
}

export interface AdminGameQuestion {
    id: number;
    containerId?: number;
    question: string;
    answers: string[];
    correctAnswerIndex: number;
    points?: number | null;
    order: number;
    createdAt?: string;
    updatedAt?: string;
}

export interface AdminGameCard {
    id: number;
    order: number;
    points: number;
    questions: AdminGameQuestion[];
}

export interface AdminGameStructure {
    id: number;
    name: string;
    order?: number;
    cards?: AdminGameCard[];
    questions?: AdminGameQuestion[];
}

export interface AdminGameSummary {
    id: number;
    gameType: StandaloneGameType;
    title: string;
    description?: string | null;
    access: 'public' | 'private';
    difficulty: 'easy' | 'medium' | 'hard';
    entryCost: number;
    numberOfPlayers?: number | null;
    timesPlayed: number;
    questionCount: number;
    structureCount: number;
    health: 'healthy' | 'needsAttention';
    issues: string[];
    status: GameStatus;
    archivedAt?: string | null;
    creator: { id: number; name: string; nickname?: string | null; email?: string | null };
    createdAt: string;
    updatedAt: string;
}

export interface AdminGameDetail extends AdminGameSummary {
    categoryIds: number[];
    categoryTags: GameCategoryOption[];
    config: Record<string, string | number>;
    structures: AdminGameStructure[];
    questions?: AdminGameQuestion[];
}

export interface AdminGameListResponse {
    items: AdminGameSummary[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    counts: { total: number; active: number; archived: number; needsAttention: number };
}

export interface QuestionInput {
    question: string;
    answers: string[];
    correctAnswerIndex: number;
    points?: number;
}

export type GameCreatePayload = Record<string, unknown>;

const request = async <T>(path: string, init?: RequestInit): Promise<T> => {
    const response = await fetch(`${BASE_URL}${path}`, {
        ...init,
        headers: { ...getAuthHeaders(), ...(init?.headers || {}) },
    });
    if (!response.ok) throw await readApiError(response, 'Standalone game request failed');
    return response.json();
};

export const fetchStandaloneGames = (filters: Record<string, string | number | undefined>): Promise<AdminGameListResponse> => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== '' && value !== 'all') params.set(key, String(value));
    });
    return request(`admin/standalone-games${params.toString() ? `?${params}` : ''}`);
};

export const fetchStandaloneGame = (gameType: StandaloneGameType, gameId: number): Promise<AdminGameDetail> =>
    request(`admin/standalone-games/${gameType}/${gameId}`);

export const createStandaloneGame = (gameType: StandaloneGameType, payload: GameCreatePayload): Promise<AdminGameDetail> =>
    request(`admin/standalone-games/${gameType}`, { method: 'POST', body: JSON.stringify(payload) });

export const updateStandaloneGame = (gameType: StandaloneGameType, gameId: number, payload: Record<string, unknown>): Promise<AdminGameDetail> =>
    request(`admin/standalone-games/${gameType}/${gameId}`, { method: 'PATCH', body: JSON.stringify(payload) });

export const addGameQuestions = (gameType: StandaloneGameType, gameId: number, containerId: number | undefined, questions: QuestionInput[]): Promise<AdminGameDetail> =>
    request(`admin/standalone-games/${gameType}/${gameId}/questions`, { method: 'POST', body: JSON.stringify({ ...(containerId ? { containerId } : {}), questions }) });

export const updateGameQuestion = (gameType: StandaloneGameType, gameId: number, questionId: number, question: QuestionInput): Promise<AdminGameDetail> =>
    request(`admin/standalone-games/${gameType}/${gameId}/questions/${questionId}`, { method: 'PATCH', body: JSON.stringify(question) });

export const deleteGameQuestion = (gameType: StandaloneGameType, gameId: number, questionId: number): Promise<AdminGameDetail> =>
    request(`admin/standalone-games/${gameType}/${gameId}/questions/${questionId}`, { method: 'DELETE' });

export const reorderGameQuestions = (gameType: StandaloneGameType, gameId: number, containerId: number | undefined, questionIds: number[]): Promise<AdminGameDetail> =>
    request(`admin/standalone-games/${gameType}/${gameId}/questions/order`, { method: 'PUT', body: JSON.stringify({ ...(containerId ? { containerId } : {}), questionIds }) });

export const setStandaloneGameArchived = (gameType: StandaloneGameType, gameId: number, archived: boolean): Promise<AdminGameDetail> =>
    request(`admin/standalone-games/${gameType}/${gameId}/${archived ? 'archive' : 'restore'}`, { method: 'POST' });

export const addBandidoCategory = (gameId: number, payload: Record<string, unknown>): Promise<AdminGameDetail> =>
    request(`admin/standalone-games/flipCardGame/${gameId}/categories`, { method: 'POST', body: JSON.stringify(payload) });

export const renameBandidoCategory = (gameId: number, categoryId: number, name: string): Promise<AdminGameDetail> =>
    request(`admin/standalone-games/flipCardGame/${gameId}/categories/${categoryId}`, { method: 'PATCH', body: JSON.stringify({ name }) });

export const deleteBandidoCategory = (gameId: number, categoryId: number): Promise<AdminGameDetail> =>
    request(`admin/standalone-games/flipCardGame/${gameId}/categories/${categoryId}`, { method: 'DELETE' });

export const updateBandidoCard = (gameId: number, cardId: number, points: number): Promise<AdminGameDetail> =>
    request(`admin/standalone-games/flipCardGame/${gameId}/cards/${cardId}`, { method: 'PATCH', body: JSON.stringify({ points }) });

export const addBandidoTier = (gameId: number, payload: Record<string, unknown>): Promise<AdminGameDetail> =>
    request(`admin/standalone-games/flipCardGame/${gameId}/card-tiers`, { method: 'POST', body: JSON.stringify(payload) });

export const deleteBandidoTier = (gameId: number, tier: number): Promise<AdminGameDetail> =>
    request(`admin/standalone-games/flipCardGame/${gameId}/card-tiers/${tier}`, { method: 'DELETE' });

export const addRollaSet = (gameId: number, payload: Record<string, unknown>): Promise<AdminGameDetail> =>
    request(`admin/standalone-games/doubleDown/${gameId}/sets`, { method: 'POST', body: JSON.stringify(payload) });

export const renameRollaSet = (gameId: number, setId: number, name: string): Promise<AdminGameDetail> =>
    request(`admin/standalone-games/doubleDown/${gameId}/sets/${setId}`, { method: 'PATCH', body: JSON.stringify({ name }) });

export const deleteRollaSet = (gameId: number, setId: number): Promise<AdminGameDetail> =>
    request(`admin/standalone-games/doubleDown/${gameId}/sets/${setId}`, { method: 'DELETE' });

export const reorderRollaSets = (gameId: number, ids: number[]): Promise<AdminGameDetail> =>
    request(`admin/standalone-games/doubleDown/${gameId}/sets/order`, { method: 'PUT', body: JSON.stringify({ ids }) });

export const fetchGameCategories = (): Promise<GameCategoryOption[]> => request('user/categories');
